/**
 * "Search by name" — the contact-name filter, in its OWN fieldset beside
 * `ContactFilterBar` rather than inside it.
 *
 * Two reasons it is a separate component and not a slot:
 *
 * 1. A11y. `ContactFilterBar`'s `sr-only` legend is what tells assistive tech
 *    which inputs belong to "Current Position Filters" now that layout position
 *    no longer does. Name is NOT a current-position filter — it matches the
 *    contact, not their promoted position — so folding it into that fieldset
 *    would announce the wrong grouping.
 * 2. It moved here from its own full-width row below the filters (2026-08-25).
 *    On a Company Map import that row was the THIRD line of filters; joining
 *    the first line removes a line rather than adding one. See the wrap-
 *    threshold note in `ContactsTable` — that row already wraps on every common
 *    laptop width, so this is not the input that breaks it.
 *
 * `w-80` and not `w-full max-w-xs`: `w-full` on a flex item resolves against
 * the container and fights `flex-wrap`. 320px is the width `max-w-xs` was
 * already capping it to, so this preserves the size without the interaction.
 *
 * Fully controlled and presentational, like its sibling: the state, the 300 ms
 * debounce and the page-1 reset all live in `ImportDetailPage`.
 */
import { filterInputClass } from "./filterInputClass";

export function NameSearchBar({
  nameQuery,
  onNameQueryChange,
}: {
  nameQuery: string;
  onNameQueryChange: (value: string) => void;
}) {
  return (
    <fieldset data-testid="name-search-filter" className="flex flex-row items-center gap-3">
      {/* `sr-only`, for the same measured reason as `ContactFilterBar`: a
          RENDERED legend is pulled out of its fieldset's flex formatting
          context and the row grows. There is no visible label twin here —
          the placeholder carries the copy — so the legend is the accessible
          name and the input keeps its own `aria-label`. */}
      <legend className="sr-only">Search contacts by name</legend>
      <input
        type="text"
        data-testid="contacts-name-search"
        aria-label="Search contacts by name"
        placeholder="Search by name…"
        value={nameQuery}
        onChange={(e) => onNameQueryChange(e.target.value)}
        className={`w-80 ${filterInputClass}`}
      />
    </fieldset>
  );
}
